import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";

const PAD = 8; // spotlight padding around the target element
const TW = 330; // tooltip width
const TH = 200; // estimated tooltip height used for placement maths

/**
 * Renders the spotlight + tooltip for the active tour. Works above Radix
 * dialogs (z-index > 50) and follows the target element on scroll/resize —
 * including scrolls inside a scrollable dialog (capture-phase listener).
 */
export default function TourOverlay({ steps, onClose }) {
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const elRef = useRef(null);
  const overlayRef = useRef(null);
  const step = steps[idx];

  // Locate the step's target: run onEnter (may open a dialog), then poll for
  // the element. If it never appears (e.g. user lacks rights), end gracefully.
  useEffect(() => {
    let cancelled = false;
    setRect(null);
    elRef.current = null;
    // Drop any focus so the mobile keyboard never sits over the tour.
    const blur = () => {
      const a = document.activeElement;
      if (a && a !== document.body && typeof a.blur === "function") a.blur();
    };
    blur();
    (async () => {
      try {
        if (step.onEnter) await step.onEnter();
      } catch {
        /* non-fatal — keep polling for the target anyway */
      }
      const t0 = Date.now();
      const poll = () => {
        if (cancelled) return;
        const el = document.querySelector(step.target);
        if (el) {
          elRef.current = el;
          blur();
          el.scrollIntoView({ block: "center", behavior: "auto" });
          requestAnimationFrame(() => {
            if (!cancelled) setRect(el.getBoundingClientRect());
          });
        } else if (Date.now() - t0 < 6000) {
          setTimeout(poll, step.optional ? 60 : 120);
        } else if (step.optional) {
          // Optional step (e.g. a section only some yachts have) — skip it.
          setIdx((i) => (i + 1 < steps.length ? i + 1 : i));
          if (idx + 1 >= steps.length) onClose();
        } else {
          toast.error("Couldn't find this part of the page — ending the tutorial.");
          onClose();
        }
      };
      poll();
    })();
    return () => {
      cancelled = true;
    };
  }, [idx, step, onClose]);

  // Keep the spotlight glued to the element on scroll / resize / dialog
  // animations. The interval covers layout shifts no event reports.
  useEffect(() => {
    const update = () => {
      if (elRef.current) setRect(elRef.current.getBoundingClientRect());
    };
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    const iv = setInterval(update, 250);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
      clearInterval(iv);
    };
  }, []);

  // Stop pointer/mouse/touch events from escaping the overlay to `document`,
  // where Radix's DismissableLayer listens. Without this, clicking a tour
  // control (Next/Back) reads as an "interaction outside" the dialog and Radix
  // auto-closes it — breaking every in-dialog step after the first. A NATIVE
  // bubble-phase listener on the overlay root fires AFTER the tour buttons have
  // received the event (so their onClick still works) but BEFORE it reaches
  // document (so Radix never sees it). Native (not React synthetic) because the
  // overlay is portaled outside the dialog and we must guarantee the real DOM
  // event is halted.
  useEffect(() => {
    const node = overlayRef.current;
    if (!node) return undefined;
    const stop = (e) => e.stopPropagation();
    node.addEventListener("pointerdown", stop);
    node.addEventListener("mousedown", stop);
    node.addEventListener("touchstart", stop);
    return () => {
      node.removeEventListener("pointerdown", stop);
      node.removeEventListener("mousedown", stop);
      node.removeEventListener("touchstart", stop);
    };
  }, []);


  if (!step) return null;

  // ---- tooltip placement ----
  // Mobile: dock as a bottom sheet — element-relative tooltips get cramped
  // on small screens. Desktop: below → above → beside, clamped to viewport.
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const isMobile = vw < 640;
  let tipStyle;
  if (isMobile) {
    tipStyle = { left: 8, right: 8, bottom: 8 };
  } else {
    tipStyle = { top: vh / 2 - TH / 2, left: vw / 2 - TW / 2, width: TW };
    if (rect) {
      const below = rect.bottom + PAD + 14;
      const above = rect.top - PAD - 14 - TH;
      let top;
      let left = Math.min(Math.max(rect.left + rect.width / 2 - TW / 2, 8), vw - TW - 8);
      if (below + TH <= vh - 8) {
        top = below;
      } else if (above >= 8) {
        top = above;
      } else {
        // Tall element — place the tooltip beside it, vertically centred.
        top = Math.min(Math.max(rect.top + rect.height / 2 - TH / 2, 8), vh - TH - 8);
        left = rect.right + PAD + 14 + TW <= vw ? rect.right + PAD + 14 : Math.max(rect.left - PAD - 14 - TW, 8);
      }
      tipStyle = { top, left, width: TW };
    }
  }

  const last = idx === steps.length - 1;

  return createPortal(
    <div
      ref={overlayRef}
      data-testid="tour-overlay"
    >
      {/* click blocker — swallows interactions with the rest of the page.
          pointerEvents must be explicit: Radix sets pointer-events:none on
          body while a dialog is open. */}
      <div className="fixed inset-0" style={{ zIndex: 100000, pointerEvents: "auto" }} />

      {/* spotlight */}
      {rect ? (
        <div
          className="fixed rounded-lg border-2 border-white/80 transition-all duration-200"
          style={{
            zIndex: 100001,
            pointerEvents: "none",
            top: rect.top - PAD,
            left: rect.left - PAD,
            width: rect.width + PAD * 2,
            height: rect.height + PAD * 2,
            boxShadow: "0 0 0 9999px rgba(9, 9, 11, 0.55)",
          }}
        />
      ) : (
        <div className="fixed inset-0" style={{ zIndex: 100001, pointerEvents: "none", background: "rgba(9,9,11,0.55)" }} />
      )}

      {/* tooltip */}
      <div
        className="fixed rounded-xl bg-white shadow-2xl border border-zinc-200 p-4"
        style={{ ...tipStyle, zIndex: 100002, pointerEvents: "auto" }}
        data-testid="tour-tooltip"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500" data-testid="tour-step-count">
            Step {idx + 1} of {steps.length}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-zinc-400 hover:text-zinc-700 -mt-1 -mr-1 p-1 rounded"
            aria-label="Skip tutorial"
            data-testid="tour-skip-btn"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="font-display text-base font-medium mt-1">{step.title}</div>
        <div className="text-[13px] text-zinc-600 mt-1.5 leading-snug">{step.body}</div>
        <div className="mt-4 flex items-center justify-between">
          <div className="flex gap-1">
            {steps.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 w-1.5 rounded-full ${i === idx ? "bg-ink" : "bg-zinc-200"}`}
              />
            ))}
          </div>
          <div className="flex gap-2">
            {idx > 0 && (
              <Button variant="outline" size="sm" onClick={() => setIdx((i) => i - 1)} data-testid="tour-back-btn">
                Back
              </Button>
            )}
            <Button
              size="sm"
              className="bg-sky-300 hover:bg-sky-400 text-white"
              onClick={() => (last ? onClose() : setIdx((i) => i + 1))}
              data-testid="tour-next-btn"
            >
              {last ? "Finish" : "Next"}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
